import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import Input from '../components/ui/Input';
import { post } from '../shared/apiClient';
import {
  ACTION_COL_CLASS,
  labelClassAuth,
  primaryCtaClassName,
  REGISTER_CARD_CLASS,
} from './register/registerShared';

const PW_RE = /^(?=.*[A-Z])(?=.*\d).{8,}$/;

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!password || !confirmPassword) return;
      if (!PW_RE.test(password)) {
        setError(t('reset_password.password_rules'));
        return;
      }
      if (password !== confirmPassword) {
        setError(t('reset_password.password_mismatch'));
        return;
      }
      setError(null);
      setSubmitting(true);
      try {
        await post('/auth/reset-password', { token, password, confirm_password: confirmPassword });
        navigate('/signin');
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : t('reset_password.failed');
        setError(msg);
      } finally {
        setSubmitting(false);
      }
    },
    [token, password, confirmPassword, navigate, t],
  );

  if (!token) {
    return (
      <AuthPageShell>
        <div className={REGISTER_CARD_CLASS}>
          <p className="text-center text-sm text-gray-600">{t('reset_password.invalid_link')}</p>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <div className={REGISTER_CARD_CLASS}>
        <h1 className="text-center text-[22px] font-semibold leading-tight text-gray-900">
          {t('reset_password.title')}
        </h1>
        <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-1">
            <Input
              id="reset-password"
              name="password"
              type="password"
              autoComplete="new-password"
              label={t('reset_password.new_password')}
              labelClassName={labelClassAuth}
              placeholder={t('shared.password_placeholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="!focus:border-[#6ECFCF] !focus:ring-[#6ECFCF]"
            />
            <p className="text-xs text-gray-500">{t('reset_password.password_hint')}</p>
          </div>
          <Input
            id="reset-confirm"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            label={t('reset_password.confirm_password')}
            labelClassName={labelClassAuth}
            placeholder={t('reset_password.repeat_placeholder')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="!focus:border-[#6ECFCF] !focus:ring-[#6ECFCF]"
          />
          {error ? (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          ) : null}
          <div className={ACTION_COL_CLASS}>
            <button
              type="submit"
              disabled={!password || !confirmPassword || submitting}
              className={primaryCtaClassName + ' disabled:opacity-50'}
            >
              {submitting ? t('reset_password.resetting') : t('reset_password.submit')}
            </button>
          </div>
        </form>
      </div>
    </AuthPageShell>
  );
};

export default ResetPasswordPage;
